import React, { forwardRef } from "react";
import { cn } from "../../utils/cn";

/**
 * DESIGN.md card surfaces.
 *
 * Variants:
 *  - default          white canvas, hairline border, 12px radius (content card)
 *  - surface-soft     #f8fafc background, 12px radius — tabbed feature cards / soft pricing tier
 *  - signature-coral  full-bleed coral, white type, 12px radius, 48px padding
 *  - signature-forest full-bleed forest green
 *  - hero-dark        full-bleed dark navy
 *  - cream-callout    cream beige, dark type, 10px radius, 24px padding
 *  - demo-grid        white, 10px radius, 16px padding (small product UI fragments)
 *  - cta-band-light   surface-strong gray, ink type, 12px radius, 48px padding
 */
// Signature pastels (cream/peach/mint/yellow/mustard) keep their fixed light surfaces in dark mode,
// so their text must also stay fixed-dark via `text-onSignature`. Coral/forest/surface-dark stay
// dark in both modes so `text-white` is always correct on those.
const VARIANT_CLASSES = {
  default: "bg-canvas text-ink border border-hairline rounded-lg p-6",
  "surface-soft": "bg-surface-soft text-ink rounded-lg p-8",
  "signature-coral": "bg-signature-coral text-white rounded-lg p-12",
  "signature-forest": "bg-signature-forest text-white rounded-lg p-12",
  "hero-dark": "bg-surface-dark text-white rounded-lg p-12",
  "cream-callout": "bg-signature-cream text-onSignature rounded-md p-6",
  "demo-grid": "bg-canvas text-ink border border-hairline rounded-md p-4",
  "cta-band-light": "bg-surface-strong text-ink rounded-lg p-12",
  "signature-peach": "bg-signature-peach text-onSignature rounded-md p-4",
  "signature-mint": "bg-signature-mint text-onSignature rounded-md p-4",
  "signature-yellow": "bg-signature-yellow text-onSignature rounded-md p-4",
  "signature-mustard": "bg-signature-mustard text-onSignature rounded-md p-4",
};

export const Card = forwardRef(function Card(
  { as: As = "div", variant = "default", className, children, ...rest },
  ref
) {
  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.default;
  return (
    <As ref={ref} className={cn(variantClass, className)} {...rest}>
      {children}
    </As>
  );
});

export default Card;
